import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService, ThirdStepData } from "@/services/auth.service/auth.api";
import { CreditCard, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Assinatura() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [userIp, setUserIp] = useState("127.0.0.1");

  const [formData, setFormData] = useState({
    cardNumber: "",
    cardName: "",
    cardExpiry: "",
    cardCvc: "",
    cep: "",
    street: "",
    number: "",
    neighborhood: "",
    city: "",
    state: "",
    complement: "",
  });

  useEffect(() => {
    const user = authService.getCurrentUser() as { _id?: string } | null;
    if (!user?._id) {
      navigate("/login");
      return;
    }
    fetch("https://api.ipify.org?format=json")
      .then((r) => r.json())
      .then((data) => setUserIp(data.ip || "127.0.0.1"))
      .catch(() => {});
  }, [navigate]);

  const handleChange = (field: string, value: string) => {
    if (field === "cardNumber") {
      const formatted = value
        .replace(/\s/g, "")
        .replace(/(\d{4})/g, "$1 ")
        .trim();
      setFormData((prev) => ({ ...prev, cardNumber: formatted }));
    } else if (field === "cardExpiry") {
      let v = value.replace(/\D/g, "");
      if (v.length > 2) v = `${v.slice(0, 2)}/${v.slice(2, 6)}`;
      setFormData((prev) => ({ ...prev, cardExpiry: v }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const searchCEP = async () => {
    if (!formData.cep || formData.cep.replace(/\D/g, "").length < 8) return;
    setIsLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${formData.cep.replace(/\D/g, "")}/json/`);
      const data = await res.json();
      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        street: data.logradouro || prev.street,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: data.uf || prev.state,
      }));
    } catch {
      toast.error("Erro ao buscar CEP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const address = {
      street: formData.street,
      number: formData.number,
      neighborhood: formData.neighborhood,
      zipcode: formData.cep.replace(/\D/g, ""),
      city: formData.city,
      state: formData.state,
      complement: formData.complement || "",
    };

    if (
      !formData.cardNumber ||
      !formData.cardName ||
      !formData.cardExpiry ||
      !formData.cardCvc ||
      !address.street ||
      !address.number ||
      !address.neighborhood ||
      !address.zipcode ||
      !address.city ||
      !address.state
    ) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    const user = authService.getCurrentUser() as { _id?: string } | null;
    if (!user?._id) {
      toast.error("Sessão inválida. Faça login novamente.");
      navigate("/login");
      return;
    }

    const payload: ThirdStepData = {
      paymentCreditCard: {
        number: formData.cardNumber.replace(/\s/g, ""),
        holder: formData.cardName,
        expirationDate: formData.cardExpiry,
        cvv: formData.cardCvc,
      },
      address,
      ip: userIp,
    };

    setIsLoading(true);
    try {
      const [, error] = await authService.thirdStep(user._id, payload);
      if (error) {
        toast.error(error.msg || "Erro ao processar pagamento", {
          style: {
            backgroundColor: "hsl(var(--destructive))",
            color: "hsl(var(--destructive-foreground))",
            borderColor: "hsl(var(--destructive))",
          },
        });
        return;
      }
      toast.success("Pagamento configurado com sucesso.");
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao processar pagamento.");
    } finally {
      setIsLoading(false);
    }
  };

  const user = authService.getCurrentUser() as { _id?: string } | null;
  if (!user?._id) return null;

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">Assinatura e pagamento</h1>
      <p className="text-muted-foreground mb-6">
        Adicione um cartão e endereço para ativar sua assinatura após o período de trial.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <h2 className="text-lg font-medium">Dados do cartão</h2>
          <div className="space-y-2">
            <Label htmlFor="cardNumber">Número do cartão</Label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="cardNumber"
                placeholder="0000 0000 0000 0000"
                value={formData.cardNumber}
                onChange={(e) => handleChange("cardNumber", e.target.value)}
                maxLength={19}
                className="pl-10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cardName">Nome no cartão</Label>
            <Input
              id="cardName"
              placeholder="NOME COMO ESTÁ NO CARTÃO"
              value={formData.cardName}
              onChange={(e) => handleChange("cardName", e.target.value.toUpperCase())}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cardExpiry">Validade (MM/AAAA)</Label>
              <Input
                id="cardExpiry"
                placeholder="MM/AAAA"
                value={formData.cardExpiry}
                onChange={(e) => handleChange("cardExpiry", e.target.value)}
                maxLength={7}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cardCvc">CVC</Label>
              <Input
                id="cardCvc"
                placeholder="000"
                value={formData.cardCvc}
                onChange={(e) => handleChange("cardCvc", e.target.value)}
                maxLength={4}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 pt-4 border-t">
          <h2 className="text-lg font-medium">Endereço</h2>
          <div className="space-y-2">
            <Label htmlFor="cep">CEP</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="cep"
                  placeholder="00000-000"
                  value={formData.cep}
                  onChange={(e) => handleChange("cep", e.target.value)}
                  className="pl-10"
                  onBlur={searchCEP}
                />
              </div>
              <Button type="button" variant="outline" onClick={searchCEP} disabled={isLoading || formData.cep.replace(/\D/g, "").length < 8}>
                Buscar
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="street">Rua</Label>
              <Input
                id="street"
                placeholder="Nome da rua"
                value={formData.street}
                onChange={(e) => handleChange("street", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="number">Número</Label>
              <Input
                id="number"
                placeholder="123"
                value={formData.number}
                onChange={(e) => handleChange("number", e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="neighborhood">Bairro</Label>
              <Input
                id="neighborhood"
                placeholder="Bairro"
                value={formData.neighborhood}
                onChange={(e) => handleChange("neighborhood", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="complement">Complemento (opcional)</Label>
              <Input
                id="complement"
                placeholder="Apto, Bloco..."
                value={formData.complement}
                onChange={(e) => handleChange("complement", e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">Cidade</Label>
              <Input
                id="city"
                placeholder="Cidade"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">Estado (UF)</Label>
              <Input
                id="state"
                placeholder="UF"
                value={formData.state}
                onChange={(e) => handleChange("state", e.target.value)}
                maxLength={2}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="submit" loading={isLoading} keyLoader="assinatura">
            Salvar e ativar assinatura
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/dashboard")}>
            Voltar
          </Button>
        </div>
      </form>
    </div>
  );
}
